import {
  Cesium3DTileset,
  HeadingPitchRoll,
  Quaternion,
  Model,
  Entity,
} from "@cesium/engine";
import EditObject from "./wrapper/EditObject";
import PointEntity from "./PointEntity";
import LinestringEntity from "./LinestringEntity";
import PolygonEntity from "./PolygonEntity";

/**
 * 객체 회전을 위한 기능 클래스.<br>
 * A functionality class for object rotation. <br>
 * ※ 주의사항(Note). </br>
 * 일반 Entity 객체에서는 기능이 제한됩니다. <br>
 * Functionality is limited on regular Entity objects..
 * @alias EditRotate
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const position = Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100);
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(position);
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * const entity = new Ditap.DitapEntity({
 *  position: position,
 *  cylinder: {
 *    length: 40.0,
 *    topRadius: 0.0,
 *    bottomRadius: 20.0,
 *    material: Color.RED,
 *  }
 * });
 * viewer.entities.add(entity);
 *
 * // 1-1. 입력된 Heading, Pitch, Roll 값으로 회전
 * const setHPR = new Ditap.HeadingPitchRoll(0, 0, 1.8);
 * EditRotate.setRotation(model, setHPR);
 * // 1-2. 입력된 Heading, Pitch, Roll 값 만큼 회전
 * const addHPR = new Ditap.HeadingPitchRoll(0, 0, 1.8);
 * EditRotate.addRotation(model, addHPR);
 * // 1-3. 입력된 Quaternion 값 으로 회전
 * const setQTN = new Ditap.Quaternion(0, 0, 1.8, 1);
 * EditRotate.setRotation(model, setQTN);
 * // 1-4. 입력된 Quaternion 값 만큼 회전
 * const addQTN = new Ditap.Quaternion(0, 0, 1.8, 1);
 * EditRotate.addRotation(model, addQTN);
 * // 1-5. Entity, DitapEntity, PointEntity, LinestringEntity, PolygonEntity 입력된 값으로 회전
 * const setEetityOrientation = Ditap.Transforms.headingPitchRollQuaternion(
 *  position,
 *  new Ditap.HeadingPitchRoll(0, 1.57, 0)
 * );
 * EditRotate.setRotation(entity, setEetityOrientation);
 * // 1-6. Entity, DitapEntity, PointEntity, LinestringEntity, PolygonEntity 입력된 값 만큼 회전.
 * EditRotate.addRotate(entity, new HeadingPitchRoll(0, 1.57, 0));
 *
 * // 2. 마우스로 객체 회전
 * const interactionRotate = new Ditap.InteractionRotate(viewer);
 * interactionRotate.on();
 */
class EditRotate {
  constructor() {
    //super();
  }

  /**
   * 모델의 헤딩, 피치, 롤 설정. </br>
   * Set HeadingPitchRoll.<br>
   * @param {Model | Cesium3DTileset | Entity | PointEntity | LinestringEntity | PolygonEntity} object 헤딩, 피치, 롤 적용할 모델.<br> The model to which to apply the heading, pitch, and roll.
   * @param {HeadingPitchRoll | Quaternion | Property} rotation 회전값.<br> Rotation value.
   * @returns {HeadingPitchRoll | Quaternion | Property} 적용된 회전.<br> Applied rotation.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * const entity = new Ditap.DitapEntity({
   *  position: position,
   *  cylinder: {
   *    length: 40.0,
   *    topRadius: 0.0,
   *    bottomRadius: 20.0,
   *    material: Color.RED,
   *  }
   * });
   * viewer.entities.add(entity);
   *
   * // Model
   * EditRotate.setRotation(model, new HeadingPitchRoll(10, 0, 0));
   *
   * // Entity
   * const setEetityOrientation = Ditap.Transforms.headingPitchRollQuaternion(
   *  position,
   *  new Ditap.HeadingPitchRoll(0, 1.57, 0)
   * );
   * EditRotate.setRotation(entity, setEetityOrientation);
   */
  static setRotation(object, rotation) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditRotate.setRotation object parameter is invalid");
    }

    try {
      // 오브젝트 업데이트
      const result = EditObject.setRotation(object, rotation);

      return result;
    } catch (error) {
      throw Error(`EditRotate.setRotation ${error}`);
    }
  }

  /**
   * 헤딩, 피치, 롤 추가.<br>
   * Add headingPitchRoll.
   * @param {Model | Cesium3DTileset | Entity | PointEntity | LinestringEntity | PolygonEntity} object 헤딩, 피치, 롤 추가할 모델.<br> The model to which to add the heading, pitch, and roll.
   * @param {HeadingPitchRoll | Quaternion} rotation 회전값.<br> Rotation value.
   * @returns {HeadingPitchRoll | Quaternion} 적용된 회전.<br> Applied rotation.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * const entity = new Ditap.DitapEntity({
   *  position: position,
   *  cylinder: {
   *    length: 40.0,
   *    topRadius: 0.0,
   *    bottomRadius: 20.0,
   *    material: Color.RED,
   *  }
   * });
   * viewer.entities.add(entity);
   *
   * EditRotate.addRotation(model, new Ditap.HeadingPitchRoll(10, 0, 0));
   */
  static addRotation(object, rotation) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditRotate.addRotation object parameter is invalid");
    }

    try {
      // Entity에는 enu를 적용하지 않고 계산.
      let enu = true;
      if (
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      ) {
        enu = false;
      }

      // HeadingPitchRoll -> Quaternion 변환
      if (rotation instanceof HeadingPitchRoll) {
        rotation = Quaternion.fromHeadingPitchRoll(rotation, new Quaternion());
      }

      // 오브젝트 Quaternion 추출
      const objectQuaternion = EditObject.getQuaternion(object, enu);

      // 오브젝트 Quaternion + 회전값
      rotation = Quaternion.multiply(
        objectQuaternion,
        rotation,
        new Quaternion()
      );

      // 오브젝트 업데이트
      const result = EditObject.setRotation(object, rotation);

      return result;
    } catch (error) {
      throw Error(`EditRotate.addRotation ${error}`);
    }
  }

  /**
   * 객체의 쿼터니언.<br>
   * Quaternion of the object.
   * @param {Model | Cesium3DTileset | Entity | PointEntity | LinestringEntity | PolygonEntity} object 쿼터니언을 구할 객체.<br> Object to calculate quaternion.
   * @param {boolean} [enu=true] East-North-Up 기준 회전값.<br> Rotation value with respect to East-North-Up.
   * @returns {Quaternion} 쿼터니언.<br> Quaternion.
   */
  static getQuaternion(object, enu = true) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditRotate.getQuaternion object parameter is invalid");
    }

    try {
      // Quaternion 추출
      const quaternion = EditObject.getQuaternion(object, enu);

      return quaternion;
    } catch (error) {
      throw Error(`EditRotate.getQuaternion ${error}`);
    }
  }

  /**
   * 객체의 헤딩-피치-롤.<br>
   * Heading-Pitch-Roll of the object.
   * @param {Model | Cesium3DTileset | Entity | PointEntity | LinestringEntity | PolygonEntity} object 헤딩-피치-롤을 구할 객체.<br> Object to calculate Heading-Pitch-Roll.
   * @param {boolean} [enu=true] East-North-Up 기준 회전값.<br> Rotation value with respect to East-North-Up.
   * @returns {HeadingPitchRoll} 헤딩-피치-롤.<br> Heading-Pitch-Roll.
   */
  static getHeadingPitchRoll(object, enu = true) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditRotate.getHeadingPitchRoll object parameter is invalid");
    }

    try {
      // HeadingPitchRoll 추출
      const headingPitchRoll = EditObject.getHeadingPitchRoll(object, enu);

      return headingPitchRoll;
    } catch (error) {
      throw Error(`EditRotate.getHeadingPitchRoll ${error}`);
    }
  }

  /**
   * 객체의 트랜스폼 추출.<br>
   * Extracting object's transform
   * @param {Model | Cesium3DTileset | Entity | PointEntity | LinestringEntity | PolygonEntity} object 트랜스폼을 구할 객체.<br> Object to obtain transform.
   * @param {boolean} [enu=true] East-North-Up 기준 추출.<br> East-North-Up criteria extraction.
   * @returns {{
   *  position: Cartesian3,
   *  originalPosition: Cartesian3,
   *  translation: Cartesian3,
   *  quaternion: Quaternion,
   *  HeadingPitchRoll: HeadingPitchRoll,
   *  scale: Cartesian3
   * }} 트랜스폼 정보.<br> Transform info.
   */
  static decomposite(object, enu = true) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditRotate.decomposite object parameter is invalid");
    }

    try {
      const decomposite = EditObject.decomposite(object, enu);

      return decomposite;
    } catch (error) {
      throw Error(`EditRotate.decomposite ${error}`);
    }
  }
}

export default EditRotate;
